import './BundleCard.scss'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useHits } from 'react-instantsearch'
import Flag from 'react-world-flags'

import IconRightArrow from '../assets/IconRightArrow.svg'
import localhostSettings from '../local'
import CoverageModal from './coverageModal/CoverageModal'
import RoamingCard from './roamingCard/RoamingCard'
import SubscribeModal from './SubscribeModal'
import { useTranslation } from 'react-i18next'

interface BundleCardProps {
  selectedCategory: string | null
  selectedCountries: string[]
}

const BundleCard: React.FC<BundleCardProps> = ({ selectedCategory, selectedCountries }) => {
  const { hits } = useHits()
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedHit, setSelectedHit] = useState<any | null>(null)
  const [isSubscribeModalOpen, setSubscribeModalOpen] = useState(false)
  const [selectedSubscribeHit, setSelectedSubscribeHit] = useState<any | null>(null)
  const [showScrollArrow, setShowScrollArrow] = useState<Record<string, boolean>>({})
  const [showLeftScrollArrow, setShowLeftScrollArrow] = useState<Record<string, boolean>>({})
  const [showSingleScrollArrow, setShowSingleScrollArrow] = useState(false)
  const [showSingleScrollLeftArrow, setShowSingleScrollLeftArrow] = useState(false)
  const isRTL = localhostSettings.isRTL()
  const { t } = useTranslation()

  function updateRightArrowVisibilityFor(category?: string) {
    const el = category ? scrollRefs.current[category] : cardRowRef.current
    if (!el) return

    const max = el.scrollWidth - el.clientWidth
    const atEnd = Math.abs(el.scrollLeft) >= max - 1
    const atStart = Math.abs(el.scrollLeft) <= 1
    if (selectedCategory && !category) {
      // single-section page
      const itemCount = groupedHits[selectedCategory]?.length || 0
      const isOverflowing = el.scrollWidth > el.clientWidth
      const shouldShow = isOverflowing && itemCount > 3 && isRTL ? !atStart : !atEnd
      const shouldLeftShow = isOverflowing && itemCount > 3 && isRTL ? !atEnd : !atStart
      setShowSingleScrollArrow((prev) => (prev === shouldShow ? prev : shouldShow))
      setShowSingleScrollLeftArrow((prev) => (prev === shouldLeftShow ? prev : shouldLeftShow))
      return
    }

    if (category) {
      const itemCount = groupedHits[category]?.length || 0
      const isOverflowing = el.scrollWidth > el.clientWidth
      const shouldShow = isOverflowing && itemCount > 3 && isRTL ? !atStart : !atEnd
      const shouldLeftShow = isOverflowing && itemCount > 3 && isRTL ? !atEnd : !atStart
      setShowScrollArrow((prev) =>
        prev[category] === shouldShow ? prev : { ...prev, [category]: shouldShow }
      )
      setShowLeftScrollArrow((prev) =>
        prev[category] === shouldLeftShow ? prev : { ...prev, [category]: shouldLeftShow }
      )
    }
  }

  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const cardRowRef = useRef<HTMLDivElement>(null)

  function scrollRightCatogry(category: string) {
    const container = scrollRefs.current[category]
    if (!container) return
    container.scrollBy({ left: 300, behavior: 'smooth' })
  }

  function scrollLeftCatogry(category: string) {
    const container = scrollRefs.current[category]
    if (!container) return
    container.scrollBy({ left: -300, behavior: 'smooth' })
  }

  function scrollRight() {
    const container = cardRowRef.current
    if (!container) return
    container.scrollBy({ left: 300, behavior: 'smooth' })
  }
  function scrollLeft() {
    const container = cardRowRef.current
    if (!container) return
    container.scrollBy({ left: -300, behavior: 'smooth' })
  }

  // Helper function to get localized card title
  function getName(hit: any): string {
    if (!hit.name) return ''
    if (typeof hit.name === 'string') return hit.name
    if (typeof hit.name === 'object') {
      return isRTL ? hit.name.valueAr : hit.name.valueEn
    }
    return ''
  }

  // ----- STABILIZE DATA -----

  const filteredHits = useMemo(() => {
    const roamingHits = hits.filter(hit => hit.addonSubTypeScope === 'ROAMING ADDON')

    const base = selectedCategory
      ? roamingHits.filter((hit) => {
        const category = hit.category?.subType
        return category && (isRTL ? category.valueAr : category.valueEn) === selectedCategory
      })
      : roamingHits

    if (selectedCountries.length === 0) return base
    const filteredCountries = base.filter((hit) =>
      hit.supportedCountryOperator?.some((op: any) =>
        selectedCountries.includes(op.countryEnglish)
      )
    )
    return filteredCountries
  }, [hits, selectedCategory, selectedCountries, isRTL])

  const groupedHits: Record<string, any[]> = useMemo(() => {
    const g: Record<string, any[]> = {}
    filteredHits.forEach(hit => {
      const subType = hit.category?.subType
      const categoryLabel = subType
        ? isRTL
          ? subType.valueAr
          : subType.valueEn
        : t('Unknown_Category')
      if (!g[categoryLabel]) g[categoryLabel] = []
      g[categoryLabel].push(hit)
    })
    return g
  }, [filteredHits, isRTL])


  const categories = useMemo(() => Object.keys(groupedHits), [groupedHits])

  // ----- STATE SET GUARDS / EFFECTS -----

  function checkOverflow(category: string) {
    const el = scrollRefs.current[category]
    const itemCount = groupedHits[category]?.length || 0
    const isOverflowing = !!el && el.scrollWidth > el.clientWidth
    const shouldShow = !!(isOverflowing && itemCount > 3)

    setShowScrollArrow((prev) => {
      if (prev[category] === shouldShow) return prev
      return { ...prev, [category]: shouldShow }
    })
    setShowLeftScrollArrow((prev) => {
      if (prev[category] === shouldShow) return prev
      return { ...prev, [category]: shouldShow }
    })
  }

  useEffect(() => {
    if (!selectedCategory || !cardRowRef.current) return

    const el = cardRowRef.current
    const isOverflowing = el.scrollWidth > el.clientWidth
    const itemCount = groupedHits[selectedCategory]?.length || 0
    const shouldShow = !!(isOverflowing && itemCount > 3)

    setShowSingleScrollArrow((prev) => (prev === shouldShow ? prev : shouldShow))
  }, [selectedCategory, groupedHits])

  useEffect(() => {
    categories.forEach(checkOverflow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories])

  useEffect(() => {
    function handleResize() {
      categories.forEach(checkOverflow)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories])

  // after rendering refs (when groups change)
  useEffect(() => {
    // initial compute + scroll listeners
    categories.forEach((category) => {
      updateRightArrowVisibilityFor(category)
      const el = scrollRefs.current[category]
      if (!el) return
      const onScroll = () => updateRightArrowVisibilityFor(category)
      el.addEventListener('scroll', onScroll)
      // cleanup
      return () => el.removeEventListener('scroll', onScroll)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories])

  // single selectedCategory row
  useEffect(() => {
    const el = cardRowRef.current
    if (!selectedCategory || !el) return

    const onScroll = () => updateRightArrowVisibilityFor()
    updateRightArrowVisibilityFor() // initial compute
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [selectedCategory, groupedHits])

  // ----- RENDER -----

  if (selectedCategory) {
    const groupHits = groupedHits[selectedCategory] || []

    return (
      <div>
        <div className='subtitle-text'>{selectedCategory}</div>
        <div className="card-scroll-container" dir={isRTL ? 'rtl' : 'ltr'}>
          {showSingleScrollLeftArrow && (
            <button className="card-scroll-button prev" onClick={() => scrollLeft()}>
              <img src={IconRightArrow} alt="Next" style={{ width: 20, height: 20 }} />
            </button>
          )}
          <div className="card-row" ref={cardRowRef}>
            {groupHits.map((hit, index) => renderRoamingCard(hit, index))}
          </div>

          {showSingleScrollArrow && (
            <button className="card-scroll-button next" onClick={() => scrollRight()}>
              <img src={IconRightArrow} alt="Next" style={{ width: 20, height: 20 }} />
            </button>
          )}
        </div>

        <CoverageModalWrapper {...{ isModalOpen, setModalOpen, selectedHit, setSelectedHit }} />
        <SubscribeModal
          isOpen={isSubscribeModalOpen}
          onClose={() => setSubscribeModalOpen(false)}
          hit={selectedSubscribeHit}
        />
      </div>
    )
  }
  return (
    <div>
      {Object.entries(groupedHits)
        .filter(([_, groupHits]) => groupHits.some((hit) => Boolean(getName(hit))))
        .map(([category, groupHits]) => (
          <div key={category} className="category-section">
            <div className='subtitle-text'>{category}</div>
            <div className="card-scroll-container">
              {showLeftScrollArrow[category] && (
                <button className="card-scroll-button prev" onClick={() => scrollLeftCatogry(category)}>
                  <img src={IconRightArrow} alt="Prev" style={{ width: 20, height: 20 }} />
                </button>
              )}
              <div className="card-row" ref={(el: any) => (scrollRefs.current[category] = el)}>
                {groupHits
                  .filter((hit) => Boolean(getName(hit)))
                  .map((hit, index) => renderRoamingCard(hit, index))}
              </div>

              {showScrollArrow[category] && (
                <button className="card-scroll-button next" onClick={() => scrollRightCatogry(category)}>
                  <img src={IconRightArrow} alt="Next" style={{ width: 20, height: 20 }} />
                </button>
              )}
            </div>
          </div>
        ))}

      <CoverageModalWrapper {...{ isModalOpen, setModalOpen, selectedHit, setSelectedHit }} />
      <SubscribeModal
        isOpen={isSubscribeModalOpen}
        onClose={() => setSubscribeModalOpen(false)}
        hit={selectedSubscribeHit}
      />
    </div>
  )

  // ----- HELPERS -----

  function renderRoamingCard(hit: any, index: number) {
    const cardTitle = getName(hit)
    const formatBenefitValue = (value?: string) => {
      if (!value) return ''
      if (!value.toUpperCase().includes('SAR') && !value.toUpperCase().includes('ريال')) return value

      return `${t('SAR')} ${value.replace(/\bSAR\b|ريال/gi, '').trim()}`
    }

    const formatBenefit = (benefit: any) => {
      const name = isRTL ? benefit.benefitNameAr : benefit.benefitNameEn
      const value = formatBenefitValue(isRTL ? benefit.valueAr : benefit.valueEn)

      return [value, name].filter(Boolean).join(' ')
    }

    const benefits =
      hit.addonBenefits
        ?.map(formatBenefit)
        .filter(Boolean) || []

    const benefitsEn =
      hit.addonBenefits
        ?.map((b: any) => (b.benefitNameEn))
        .filter(Boolean) || []

    const specs = hit.specCharacteristics || []
    const getValue = (code: string) => specs.find((s: any) => s.code === code)?.[isRTL ? 'valueAr' : 'valueEn'] || ''
    const activePeriodLength = getValue('activePeriodLength')
    const activePeriodUnits = getValue('activePeriodUnits')
    const subscriptionCommand = getValue('SUBSCRIPTION_COMMAND')

    const src = hit.oneTimePrice?.[0] ?? hit.recurringPrice?.[0] ?? {}
    const price = src.price ?? ''
    const unit = src.unit ? src.unit === 'SAR' ? t('SAR') : src.unit : t('SAR')
    // const isRecurring = !hit.oneTimePrice?.[0] && !!hit.recurringPrice?.[0]

    const getCardBgColor = (hit: any) => {
      const subTypeCode = hit?.category?.subType?.code || "";

      if (subTypeCode.includes('Internet Bundles') || subTypeCode.includes('International Bundles')) {
        return '#007bff'
      }

      if (subTypeCode.includes('Roaming Basic Bundles') || subTypeCode.includes('Connect Roaming Bundles')) {
        return '#016C71'
      }

      if (subTypeCode.includes('Tejwali')) {
        return '#01376A'
      }
    }

    return (
      cardTitle && (
        <RoamingCard
          key={index}
          cardTitle={cardTitle}
          benefits={benefits}
          benefitsEn={benefitsEn}
          validity={activePeriodLength}
          activePeriodUnits={activePeriodUnits}
          smsText={subscriptionCommand}
          price={price}
          unit={unit}
          isRTL={isRTL}
          coverageText={
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {(hit.supportedCountryOperator || []).slice(0, 2).map((country: any, idx: number) => (
                <Flag
                  key={idx}
                  code={country.id}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    marginRight: 6,
                  }}
                />
              ))}
              {hit.supportedCountryOperator?.length > 2 && (
                <span style={{ fontSize: '12px', color: '#565E63' }}>
                  & {hit.supportedCountryOperator.length - 2} {t("more")}
                </span>
              )}
            </div>
          }
          titleBackgroundColor={getCardBgColor(hit)}
          onShowCoverageModal={() => {
            setSelectedHit(hit)
            setModalOpen(true)
          }}
          buttonName={t('BUY')}
          onClickBuyButton={() => {
            setSelectedSubscribeHit(hit)
            setSubscribeModalOpen(true)
          }}
        />
      )
    )
  }

  function CoverageModalWrapper({
    isModalOpen,
    setModalOpen,
    selectedHit,
    setSelectedHit,
  }: {
    isModalOpen: boolean
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
    selectedHit: any | null
    setSelectedHit: React.Dispatch<React.SetStateAction<any | null>>
  }) {
    return (
      <CoverageModal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false)
          setSelectedHit(null)
        }}
        supportedCountries={selectedHit?.supportedCountryOperator || []}
      />
    )
  }
}

export default BundleCard
